import React from 'react';
import { styled } from '@mui/material/styles';
import { IconButton } from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import clsx from 'clsx';
import { useAuth } from 'lib/useAuth';
import { AppBarUserMenu } from 'lib/appbar/AppBarUserMenu';

const PREFIX = 'PayAvatar';

const classes = {
    icon: `${PREFIX}icon`,
    userAvatar: `${PREFIX}userAvatar`,
    userInitials: `${PREFIX}userInitials`
};

const Root = styled('div')(({ theme }) => ({
    [`& .${classes.userAvatar}`]: {
        width: '24px',
        height: '24px',
        borderRadius: '50%'
    },

    [`& .${classes.userInitials}`]: {
        fontSize: theme.spacing(1.5),
        fontWeight: theme.typography.fontWeightBold,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
}));

interface PayAvatarProps {
    onAuthActionClick?: () => Promise<void>;
    className?: string;
}

export const PayAvatar: React.FC<React.PropsWithChildren<PayAvatarProps>> = ({
    onAuthActionClick,
    className
}) => {
    const menuId = 'pay-menu-id';
    const { isLoggedIn, user } = useAuth();
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | undefined>(undefined);
    const [isAvatarAvailable, setIsAvatarAvailable] = React.useState<boolean>(true);
    const userId = React.useMemo(() => (user ? user._id : ''), [user]);
    const userInitials = React.useMemo(() => user && user.firstName[0] + user.lastName[0], [user]);
    const handleOpen = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl((value?: HTMLElement) => (!!value ? undefined : e.currentTarget));
    }, []);
    const closeMenu = React.useCallback(() => setAnchorEl(undefined), []);
    const handleError = React.useCallback(() => {
        setIsAvatarAvailable(false);
    }, []);
    return (
        <Root>
            <IconButton
                className={className}
                aria-label="show more"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleOpen}
                size="large"
            >
                {isLoggedIn &&
                    (isAvatarAvailable ? (
                        <img
                            className={classes.userAvatar}
                            src={`${process.env.MEDIA_URL}/users/avatars/${userId}.jpg`}
                            onError={handleError}
                        />
                    ) : (
                        <span className={clsx(classes.userAvatar, classes.userInitials)}>{userInitials}</span>
                    ))}
                {!isLoggedIn && <AccountCircle color="inherit" />}
            </IconButton>
            {!!anchorEl && (
                <AppBarUserMenu
                    anchorEl={anchorEl}
                    menuId={menuId}
                    onClose={closeMenu}
                    onAuthActionClick={onAuthActionClick}
                />
            )}
        </Root>
    );
};
